import React, { useState, useCallback } from 'react';
import { SharedHeader, TimeframePanel } from '../components/IndicatorDashboard';
import { fetchHistorical } from '../api';
import { useAppData } from '../AppDataContext';

const TIMEFRAMES = ['5m', '3m'];

export default function Indicators() {
  const { indicatorsByTf } = useAppData();
  const liveByTf = indicatorsByTf;
  const loading = Object.keys(liveByTf).length === 0;

  const [mode, setMode] = useState('live');

  // ── Historical ──
  const [histDate, setHistDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 1);
    while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() - 1);
    return d.toISOString().split('T')[0];
  });
  const [histDataByTf, setHistDataByTf] = useState({});
  const [histLoading, setHistLoading] = useState(false);
  const [histError, setHistError] = useState(null);
  const [selectedSnapByTf, setSelectedSnapByTf] = useState({});

  const loadHistorical = useCallback(async () => {
    setHistLoading(true);
    setHistError(null);
    setHistDataByTf({});
    setSelectedSnapByTf({});
    try {
      const results = await Promise.all(TIMEFRAMES.map(t => fetchHistorical(histDate, t)));
      const nextData = {};
      const nextSelected = {};
      results.forEach((res, idx) => {
        const t = TIMEFRAMES[idx];
        if (res.data?.status === 'ok') {
          nextData[t] = res.data;
          if (res.data.snapshots?.length) {
            nextSelected[t] = res.data.snapshots[res.data.snapshots.length - 1];
          }
        }
      });
      setHistDataByTf(nextData);
      setSelectedSnapByTf(nextSelected);
      if (!Object.keys(nextData).length) setHistError('No historical data for this date.');
    } catch {
      setHistError('Failed to load historical data');
    }
    setHistLoading(false);
  }, [histDate]);

  // Pick data per mode for each TF
  const data5m = mode === 'live' ? liveByTf['5m'] : selectedSnapByTf['5m'];
  const data3m = mode === 'live' ? liveByTf['3m'] : selectedSnapByTf['3m'];
  const sharedData = data5m || data3m; // shared header just needs one

  // Historical: select snapshot at index (try matching same time across TFs)
  const setSnapByIdx = (idx) => {
    setSelectedSnapByTf(prev => {
      const next = { ...prev };
      TIMEFRAMES.forEach(t => {
        const snaps = histDataByTf[t]?.snapshots || [];
        if (snaps.length) next[t] = snaps[Math.min(idx, snaps.length - 1)];
      });
      return next;
    });
  };

  // Build a unified timeline from the 5m snapshots (or 3m if 5m missing)
  const timelineSnaps = histDataByTf['5m']?.snapshots || histDataByTf['3m']?.snapshots || [];
  const selectedIdx = timelineSnaps.findIndex(s =>
    s === selectedSnapByTf['5m'] || s === selectedSnapByTf['3m']
  );

  return (
    <div className="page-indicators ind-page">
      {/* ── Header ── */}
      <div className="ind-header">
        <div className="ind-header-left">
          <h1 className="ind-page-title">📊 Market Indicators</h1>
          <p className="ind-page-sub">
            {mode === 'live'
              ? 'Live · 5-min and 3-min signals side by side'
              : `Replay snapshot from ${histDate}`}
          </p>
        </div>
        <div className="ind-header-right">
          <div className="ind-seg">
            <button className={mode === 'live' ? 'on' : ''} onClick={() => setMode('live')}>● Live</button>
            <button className={mode === 'historical' ? 'on' : ''} onClick={() => setMode('historical')}>⏱ Historical</button>
          </div>
        </div>
      </div>

      {/* ── Historical controls ── */}
      {mode === 'historical' && (
        <div className="ind-hist-bar">
          <input type="date" value={histDate} onChange={e => setHistDate(e.target.value)} />
          <button className="ind-btn-primary" onClick={loadHistorical} disabled={histLoading}>
            {histLoading ? 'Loading…' : 'Load Snapshots'}
          </button>
          {histError && <span className="ind-hist-error">{histError}</span>}
          {timelineSnaps.length > 0 && (
            <div className="ind-snap-timeline">
              {timelineSnaps.map((snap, i) => {
                const isSel = i === selectedIdx;
                const t = snap.timestamp?.split(' ')[1]?.substring(0, 5);
                return (
                  <button key={i} className={`ind-snap-dot ${isSel ? 'on' : ''}`}
                    title={snap.timestamp} onClick={() => setSnapByIdx(i)}>
                    {t}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Loading ── */}
      {mode === 'live' && loading && (
        <div className="ind-loading">
          <div className="ind-spinner" />
          <p>Connecting to live data feed…</p>
        </div>
      )}

      {/* ── Snapshot banner (historical) ── */}
      {mode === 'historical' && selectedSnapByTf['5m'] && (
        <div className="ind-time-banner">
          Snapshot @ <strong>{selectedSnapByTf['5m'].timestamp}</strong>
        </div>
      )}

      {/* ── Shared section ── */}
      {sharedData && <SharedHeader data={sharedData} />}

      {/* ── Side-by-side timeframe comparison ── */}
      {(data5m || data3m) && (
        <>
          <h3 className="ind-group-title">⏱ Timeframe Comparison</h3>
          <div className="ind-tf-compare">
            <TimeframePanel data={data5m} tfLabel="5 MIN" />
            <TimeframePanel data={data3m} tfLabel="3 MIN" />
          </div>
        </>
      )}

      {mode === 'historical' && !histLoading && !sharedData && (
        <div className="ind-empty">Select a date and click <strong>Load Snapshots</strong> to view indicator history.</div>
      )}
    </div>
  );
}
